"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import {
	AhaApplySignupCard,
	ahaApplyAskCopy,
	markAhaApplyAskSkipped,
	wasAhaApplyAskSkipped,
} from "@/components/jobs/aha-apply-signup-card";
import { useJobContext } from "@/components/jobs/job-context";
import { APPLY_CLICKED_EVENT } from "@/components/monetization/post-apply-offer";
import {
	exposureFiredToday,
	markExposureFiredToday,
} from "@/lib/analytics/use-experiment";
import {
	AHA_SIGNUP_ASK_CLICKED_EVENT,
	AHA_SIGNUP_ASK_FLAG,
	AHA_SIGNUP_ASK_SHOWN_EVENT,
	type AhaSignupAskArm,
	hasSeenAhaSignupAsk,
	markAhaSignupAskSeen,
	readAhaSignupAskArm,
	readAhaSignupAskArmNow,
} from "@/lib/experiments/aha-signup-ask";
import {
	claimApplyMomentForIntentSignup,
	JOB_INTENT_SIGNUP_SRC,
} from "@/lib/experiments/job-intent-signup";
import { upsertApplication } from "@/server/actions/applications";

/**
 * `aha-signup-ask` treatment arm, apply trigger: the post-apply signup card,
 * plus the return leg after signup. See `src/lib/experiments/aha-signup-ask.ts`
 * for the experiment and `src/lib/experiments/job-intent-signup.ts` for why
 * this cannot reproduce the retired `apply_signup_gate`.
 *
 * Rendered inside `JobApplyCta`'s `wrap()` (sidebar + mobile footer). Only
 * the on-screen instance (`onScreen`) listens, exposes or renders, the same
 * rule `autoPrepareOnReturn` applies to the email handoff.
 */

/** One return-save per job per page lifetime, across both CTA instances. */
const returnHandled = new Set<string>();
/** Exported for tests: the set outlives a test. */
export function resetJobIntentSignupReturnGuard(): void {
	returnHandled.clear();
}

type Props = {
	jobId: string;
	/** Locale-correct, slug-bearing detail path (no query). */
	jobPathname: string;
	locale: string;
	placement: "sidebar" | "footer";
	/** Resolved guest (`!loading && userState === "guest"`), external, non-email apply. */
	eligible: boolean;
	/** This instance is the one actually on screen. */
	onScreen: boolean;
};

export function JobIntentSignup({
	jobId,
	jobPathname,
	locale,
	placement,
	eligible,
	onScreen,
}: Props) {
	const posthog = usePostHog();
	const { loading, isAuthenticated, isAlreadyApplied } = useJobContext();
	const t = ahaApplyAskCopy(locale);
	const evLocale = locale === "en" ? "en" : "id";
	const [shown, setShown] = useState(false);
	const [returnSaved, setReturnSaved] = useState(false);
	// Latest gating for the window listener without re-subscribing per render.
	const liveRef = useRef({ eligible, onScreen });
	liveRef.current = { eligible, onScreen };

	const callbackURL = `${jobPathname}?src=${JOB_INTENT_SIGNUP_SRC}`;

	// Arm is read AT THE APPLY CLICK, never on mount: `$feature_flag_called`
	// and `experiment_exposure` then both mean "a guest clicked Apply".
	useEffect(() => {
		if (!posthog) return;
		let cancelled = false;
		const expose = (arm: AhaSignupAskArm) => {
			if (!exposureFiredToday(AHA_SIGNUP_ASK_FLAG)) {
				markExposureFiredToday(AHA_SIGNUP_ASK_FLAG);
				posthog.capture("experiment_exposure", {
					experiment: AHA_SIGNUP_ASK_FLAG,
					variant: arm,
					trigger: "apply",
					job_id: jobId,
					surface: "job_detail_apply",
				});
			}
			// Once per person, across both triggers; a skip also counts.
			if (
				arm !== "treatment" ||
				wasAhaApplyAskSkipped() ||
				hasSeenAhaSignupAsk()
			) {
				return;
			}
			markAhaSignupAskSeen("apply");
			claimApplyMomentForIntentSignup(jobId);
			setShown(true);
		};
		const onApply = (event: Event) => {
			const detail = (event as CustomEvent<{ jobId?: string }>).detail;
			if (detail?.jobId !== jobId) return;
			const live = liveRef.current;
			if (!live.eligible || !live.onScreen) return;
			// Synchronous path (flags already loaded, the normal case) so the
			// apply-moment claim lands inside this dispatch.
			const sync = readAhaSignupAskArmNow(posthog);
			if (sync) {
				expose(sync);
				return;
			}
			void readAhaSignupAskArm(posthog).then((arm) => {
				if (!cancelled && arm) expose(arm);
			});
		};
		window.addEventListener(APPLY_CLICKED_EVENT, onApply);
		return () => {
			cancelled = true;
			window.removeEventListener(APPLY_CLICKED_EVENT, onApply);
		};
	}, [posthog, jobId]);

	useEffect(() => {
		if (!shown) return;
		posthog?.capture(AHA_SIGNUP_ASK_SHOWN_EVENT, {
			trigger: "apply",
			job_id: jobId,
			locale: evLocale,
			placement,
		});
	}, [shown, posthog, jobId, evLocale, placement]);

	// Return leg: back from signup with ?src=job_intent_signup. Save this job
	// as a `clicked` tracker row (feeds the did-you-apply follow-up), unless
	// it is already tracked. Read from window.location, never searchParams:
	// the page is ISR-cached as the guest view.
	useEffect(() => {
		if (!onScreen || loading || !isAuthenticated) return;
		if (returnHandled.has(jobId)) return;
		let src: string | null = null;
		try {
			src = new URLSearchParams(window.location.search).get("src");
		} catch {
			return;
		}
		if (src !== JOB_INTENT_SIGNUP_SRC) return;
		returnHandled.add(jobId);
		try {
			const url = new URL(window.location.href);
			url.searchParams.delete("src");
			window.history.replaceState(
				window.history.state,
				"",
				`${url.pathname}${url.search}${url.hash}`,
			);
		} catch {
			/* leave the param; the Set still prevents a second save */
		}
		const report = (saved: boolean, alreadyTracked: boolean) =>
			posthog?.capture("job_intent_signup_returned", {
				job_id: jobId,
				locale: evLocale,
				saved,
				already_tracked: alreadyTracked,
			});
		if (isAlreadyApplied) {
			setReturnSaved(true);
			report(true, true);
			return;
		}
		upsertApplication(jobId, "clicked")
			.then((res) => {
				const ok = !("error" in res);
				setReturnSaved(ok);
				report(ok, false);
			})
			.catch(() => report(false, false));
	}, [
		onScreen,
		loading,
		isAuthenticated,
		isAlreadyApplied,
		jobId,
		posthog,
		evLocale,
	]);

	if (!onScreen) return null;

	if (returnSaved) {
		return (
			<p
				role="status"
				data-testid="job-intent-signup-return"
				className="mt-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-foreground"
			>
				{t.saved}
			</p>
		);
	}

	if (!shown) return null;

	const onSkip = () => {
		markAhaApplyAskSkipped();
		setShown(false);
		posthog?.capture("job_intent_signup_skipped", {
			job_id: jobId,
			locale: evLocale,
			placement,
		});
	};

	const onAskClicked = (method: "google" | "email") =>
		posthog?.capture(AHA_SIGNUP_ASK_CLICKED_EVENT, {
			trigger: "apply",
			method,
			job_id: jobId,
			locale: evLocale,
			placement,
		});

	return (
		<AhaApplySignupCard
			locale={locale}
			callbackURL={callbackURL}
			showEyebrow={placement === "sidebar"}
			testId="job-intent-signup"
			placement={placement}
			onAskClicked={onAskClicked}
			onSkip={onSkip}
		/>
	);
}
